// src/components/dashboard/widgets/LowStockWidget.js
// Version: 2.1 — WebSocket sync + isMounted ref + autoRefreshInterval default
// ─────────────────────────────────────────────────────────────────────────────
// MERGE NOTES (v2.0 Doc14 → v2.1):
//
//   FROM Doc 13:
//   - webSocketService imported and subscribed to 'inventory_updated':
//     any backend inventory change immediately triggers a silent re-fetch
//     without waiting for the next poll interval — keeps the widget live.
//   - autoRefreshInterval default changed from null → 60000 ms so the widget
//     auto-polls out of the box just like the original v1 behaviour.
//
//   BUG FIX (Doc 14 imported useRef but never used it):
//   - isMounted ref added; every setState call in fetchData is now gated with
//     if (!isMounted.current) return — prevents setState-after-unmount warnings
//     on fast tab switches or component removal.
//   - Mount/unmount effect sets isMounted.current = false on cleanup.
//
//   EVERYTHING from Doc 14 is preserved unchanged:
//   localStorage zero-flicker initialisation, showDebug prop, tab-visibility
//   pause, deep-equality check before setState, complex stats object,
//   getStockIcon / getStockColor, suggested reorder quantity calculation,
//   full UI (stats grid, list items with badges, footer legend).
//
//   CORE API: inventoryService.getLowStockAlerts() — ZERO CHANGES
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, Spinner, Flex, List, ListItem, Icon, Button,
  Alert, AlertIcon, Badge, Progress, VStack, HStack, Stat, StatLabel,
  StatNumber, Tooltip, IconButton, useColorModeValue, SimpleGrid, useToast,
} from '@chakra-ui/react';
import {
  MdOutlineInventory, MdWarning, MdCheckCircle, MdRefresh, MdShoppingCart,
} from 'react-icons/md';
import { FaBoxes, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import inventoryService  from '../../../services/inventoryService';
// --- UPDATE: WebSocket service for live inventory change events (from Doc 13) ---
import webSocketService from '../../../services/websocketService';
// --- END UPDATE ---

// --- UPDATE: autoRefreshInterval default changed null → 60000 (from Doc 13) ---
const LowStockWidget = ({ showDebug = false, autoRefreshInterval = 60000 }) => {
// --- END UPDATE ---

  // ── State — zero-flicker via localStorage (🔒 Doc 14 preserved) ───────────
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('flowSense_lowStock_items');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [stats, setStats] = useState(() => {
    try {
      const saved = localStorage.getItem('flowSense_lowStock_stats');
      return saved
        ? JSON.parse(saved)
        : { totalItems: 0, lowStockCount: 0, criticalCount: 0, wellStockedCount: 0 };
    } catch {
      return { totalItems: 0, lowStockCount: 0, criticalCount: 0, wellStockedCount: 0 };
    }
  });

  const [lastUpdated, setLastUpdated] = useState(() => {
    try {
      const saved = localStorage.getItem('flowSense_lowStock_lastUpdated');
      return saved ? new Date(saved) : null;
    } catch { return null; }
  });

  // Only show full spinner when cache is completely empty
  const [isLoading, setIsLoading] = useState(
    !localStorage.getItem('flowSense_lowStock_items')
  );
  const [error,       setError]       = useState(null);
  const [rawResponse, setRawResponse] = useState(null);

  const toast    = useToast();
  const navigate = useNavigate();

  // ── Refs ───────────────────────────────────────────────────────────────────
  // --- UPDATE: isMounted ref — Doc 14 imported useRef but never used it ---
  const isMounted = useRef(true);
  // --- END UPDATE ---

  // ── Theme ──────────────────────────────────────────────────────────────────
  const bgColor     = useColorModeValue('white',    'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor   = useColorModeValue('gray.600', 'gray.400');
  const criticalBg  = useColorModeValue('red.50',   'red.900');
  const warningBg   = useColorModeValue('orange.50','orange.900');

  // ── CORE: fetchData (🔒 Doc 14 logic preserved, isMounted guards added) ───
  const fetchData = useCallback(async (isRefresh = false) => {
    if (!localStorage.getItem('flowSense_lowStock_items') || isRefresh) {
      setIsLoading(true);
    }
    setError(null);

    if (isRefresh) {
      toast({
        title:       'Refreshing Inventory',
        description: 'Checking stock levels...',
        status:      'info',
        duration:    1500,
      });
    }

    try {
      if (showDebug) console.log('[LowStock] Fetching low stock alerts...');

      const data = await inventoryService.getLowStockAlerts();

      // --- UPDATE: isMounted guard — prevents setState after unmount ---
      if (!isMounted.current) return;
      // --- END UPDATE ---

      if (showDebug) setRawResponse(data);

      const alerts = data.alerts || [];

      // Process items (🔒 Doc 14 logic preserved)
      const lowStockItems = alerts.map(alert => {
        const item        = alert.item || alert;
        const quantity    = parseInt(item.quantity || item.current_quantity || 0, 10);
        const threshold   = parseInt(item.low_stock_threshold || item.threshold  || 0, 10);
        const stockPercentage = threshold > 0 ? (quantity / threshold) * 100 : 100;

        return {
          ...item,
          id:                  item.id  || alert.id,
          name:                item.name || item.item_name || 'Unknown Item',
          quantity,
          low_stock_threshold: threshold,
          stockPercentage,
          isCritical:  stockPercentage <= 25,
          isWarning:   stockPercentage > 25 && stockPercentage <= 50,
          stockLevel:  stockPercentage <= 25 ? 'critical'
                     : stockPercentage <= 50 ? 'warning'
                     : stockPercentage <= 75 ? 'low'
                     :                         'normal',
        };
      });

      // Sort by stock percentage (most critical first)
      lowStockItems.sort((a, b) => a.stockPercentage - b.stockPercentage);

      const newStats = {
        totalItems:      lowStockItems.length,
        lowStockCount:   lowStockItems.filter(i => i.stockPercentage <= 100).length,
        criticalCount:   lowStockItems.filter(i => i.isCritical).length,
        wellStockedCount:lowStockItems.filter(i => i.stockPercentage > 75).length,
      };

      // Deep equality check — skip re-render if data unchanged (🔒 Doc 14)
      const newItemsString = JSON.stringify(lowStockItems);
      const oldItemsString = localStorage.getItem('flowSense_lowStock_items');

      if (newItemsString !== oldItemsString || isRefresh) {
        const timestamp = new Date();

        // --- UPDATE: isMounted guard before each setState ---
        if (!isMounted.current) return;
        // --- END UPDATE ---

        setItems(lowStockItems);
        setStats(newStats);
        setLastUpdated(timestamp);

        // Update localStorage cache
        localStorage.setItem('flowSense_lowStock_items',       newItemsString);
        localStorage.setItem('flowSense_lowStock_stats',       JSON.stringify(newStats));
        localStorage.setItem('flowSense_lowStock_lastUpdated', timestamp.toISOString());

        // Toast alert on new critical items during a refresh
        if (isRefresh && newStats.criticalCount > 0) {
          toast({
            title:       'Critical Stock Alert',
            description: `${newStats.criticalCount} item(s) are critically low`,
            status:      'warning',
            duration:    5000,
            isClosable:  true,
          });
        }
      }
    } catch (err) {
      console.error('[LowStock] Failed to fetch low stock items:', err);
      // --- UPDATE: isMounted guard before error setState ---
      if (!isMounted.current) return;
      // --- END UPDATE ---
      setError(err.message || 'Failed to load inventory data');
      toast({
        title:       'Error loading inventory',
        description: err.message,
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      // --- UPDATE: isMounted guard before loading setState ---
      if (isMounted.current) setIsLoading(false);
      // --- END UPDATE ---
    }
  }, [toast, showDebug]);

  // ── Mount / unmount lifecycle ───────────────────────────────────────────────
  // --- UPDATE: sets isMounted false on cleanup to pair with the ref above ---
  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount — fetchData is stable
  // --- END UPDATE ---

  // ── Tab-visibility pausing for auto-refresh (🔒 Doc 14 preserved) ─────────
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return () => {};

    let interval;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData(false);
        interval = setInterval(() => fetchData(false), autoRefreshInterval);
      } else {
        clearInterval(interval);
      }
    };

    if (document.visibilityState === 'visible') {
      interval = setInterval(() => fetchData(false), autoRefreshInterval);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefreshInterval, fetchData]);

  // --- UPDATE: WebSocket subscription — live inventory change events (Doc 13)
  //   Any backend mutation that emits 'inventory_updated' triggers a silent
  //   re-fetch immediately, without waiting for the next poll interval.
  useEffect(() => {
    const handleInventoryChange = () => {
      if (isMounted.current) fetchData(false); // silent — no refresh toast
    };

    webSocketService?.subscribe?.('inventory_updated', handleInventoryChange);
    return () => {
      webSocketService?.unsubscribe?.('inventory_updated', handleInventoryChange);
    };
  }, [fetchData]);
  // --- END UPDATE ---

  // ── Handlers (🔒 Doc 14 preserved) ────────────────────────────────────────
  const handleRefresh = () => fetchData(true);

  const handleReorder = (item) => {
    const suggestedQty = Math.max(
      (item.low_stock_threshold * 3) - item.quantity,
      1
    );
    navigate(
      `/admin/purchase-orders?action=new&itemId=${item.id}&suggestedQty=${suggestedQty}`
    );
    toast({
      title:       'Routing to Procurement',
      description: `Drafting Purchase Order for ${item.name}...`,
      status:      'info',
      duration:    2000,
      isClosable:  true,
    });
  };

  // ── Helpers (🔒 Doc 14 preserved) ─────────────────────────────────────────
  const getStockColor = (percentage) => {
    if (percentage <= 25) return 'red';
    if (percentage <= 50) return 'orange';
    if (percentage <= 75) return 'yellow';
    return 'green';
  };

  const getStockIcon = (level) => {
    switch (level) {
      case 'critical': return MdWarning;
      case 'warning':  return FaExclamationTriangle;
      case 'low':      return MdOutlineInventory;
      default:         return MdCheckCircle;
    }
  };

  // ── RENDER (🔒 Doc 14 UI preserved) ───────────────────────────────────────
  return (
    <Box h="100%">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Low Stock Alerts</Heading>
        <HStack spacing={2}>
          <Tooltip label="Refresh inventory data" hasArrow>
            <IconButton
              icon={<MdRefresh />}
              size="sm"
              onClick={handleRefresh}
              isLoading={isLoading}
              aria-label="Refresh"
              variant="outline"
            />
          </Tooltip>
          {lastUpdated && (
            <Text fontSize="xs" color={textColor}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </Text>
          )}
        </HStack>
      </Flex>

      {/* ── Debug Panel ──────────────────────────────────────────────────── */}
      {showDebug && rawResponse && (
        <Alert status="info" mb={4} fontSize="xs" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Debug Info:</Text>
            <Text>Alert count: {rawResponse.alerts?.length || 0}</Text>
          </Box>
        </Alert>
      )}

      {/* ── Error State ──────────────────────────────────────────────────── */}
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Error loading inventory</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        </Alert>
      )}

      {/* ── Statistics Summary ───────────────────────────────────────────── */}
      {!isLoading && stats.totalItems > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={4}>
          <Stat
            size="sm" p={2} borderWidth="1px" borderRadius="md"
            borderColor={borderColor}
          >
            <StatLabel fontSize="xs">Total Alerts</StatLabel>
            <StatNumber fontSize="lg">{stats.totalItems}</StatNumber>
          </Stat>
          <Stat
            size="sm" p={2} borderWidth="1px" borderRadius="md"
            borderColor="red.300" bg={criticalBg}
          >
            <StatLabel fontSize="xs">Critical</StatLabel>
            <StatNumber fontSize="lg" color="red.600">{stats.criticalCount}</StatNumber>
          </Stat>
          <Stat
            size="sm" p={2} borderWidth="1px" borderRadius="md"
            borderColor="orange.300" bg={warningBg}
          >
            <StatLabel fontSize="xs">Warning</StatLabel>
            <StatNumber fontSize="lg" color="orange.600">
              {stats.lowStockCount - stats.criticalCount}
            </StatNumber>
          </Stat>
          <Stat
            size="sm" p={2} borderWidth="1px" borderRadius="md"
            borderColor={borderColor}
          >
            <StatLabel fontSize="xs">Action Needed</StatLabel>
            <StatNumber fontSize="lg">{stats.lowStockCount}</StatNumber>
          </Stat>
        </SimpleGrid>
      )}

      {/* ── Loading State ────────────────────────────────────────────────── */}
      {isLoading ? (
        <Flex justify="center" align="center" h="200px">
          <VStack>
            <Spinner size="lg" color="blue.500" />
            <Text color={textColor}>Loading inventory data...</Text>
          </VStack>
        </Flex>
      ) : (
        <>
          {/* ── Empty State ─────────────────────────────────────────────── */}
          {items.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              h="200px"
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={borderColor}
              borderRadius="lg"
              p={6}
            >
              <Icon as={FaBoxes} w={12} h={12} color="green.500" mb={3} />
              <Text fontSize="lg" fontWeight="bold" mb={2}>
                All Items Well Stocked!
              </Text>
              <Text fontSize="sm" color={textColor} textAlign="center">
                No items are currently below their reorder threshold.
              </Text>
            </Flex>
          ) : (
            /* ── Item List ──────────────────────────────────────────────── */
            <List spacing={3} maxH="400px" overflowY="auto">
              {items.map(item => (
                <ListItem
                  key={item.id}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={item.isCritical ? criticalBg : item.isWarning ? warningBg : bgColor}
                  _hover={{ shadow: 'md' }}
                  transition="all 0.2s"
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" flex="1">
                      <Icon
                        as={getStockIcon(item.stockLevel)}
                        color={`${getStockColor(item.stockPercentage)}.500`}
                        w={6}
                        h={6}
                        mr={3}
                      />
                      <Box flex="1">
                        <HStack spacing={2} mb={1}>
                          <Text fontWeight="bold" fontSize="sm">{item.name}</Text>
                          {item.isCritical && (
                            <Badge colorScheme="red" fontSize="xs">CRITICAL</Badge>
                          )}
                          {item.isWarning && !item.isCritical && (
                            <Badge colorScheme="orange" fontSize="xs">LOW</Badge>
                          )}
                        </HStack>

                        <HStack spacing={4} mb={1}>
                          <Text fontSize="xs" color={textColor}>
                            Current: <strong>{item.quantity}</strong>
                          </Text>
                          <Text fontSize="xs" color={textColor}>
                            Threshold: <strong>{item.low_stock_threshold}</strong>
                          </Text>
                          <Text fontSize="xs" color={textColor}>
                            Stock Level: <strong>{item.stockPercentage.toFixed(0)}%</strong>
                          </Text>
                        </HStack>

                        <Progress
                          value={item.stockPercentage}
                          size="xs"
                          colorScheme={getStockColor(item.stockPercentage)}
                          borderRadius="full"
                          max={100}
                        />
                      </Box>
                    </Flex>

                    <Tooltip label={`Draft PO for ${item.name}`} hasArrow>
                      <Button
                        size="sm"
                        colorScheme={item.isCritical ? 'red' : 'blue'}
                        variant={item.isCritical ? 'solid' : 'outline'}
                        onClick={() => handleReorder(item)}
                        leftIcon={<MdShoppingCart />}
                        ml={3}
                        flexShrink={0}
                      >
                        Reorder
                      </Button>
                    </Tooltip>
                  </Flex>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}

      {/* ── Footer Legend ────────────────────────────────────────────────── */}
      {!isLoading && items.length > 0 && (
        <Box mt={4} pt={3} borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={4} fontSize="xs" color={textColor} flexWrap="wrap">
            <HStack>
              <Box w={3} h={3} bg="red.500"    borderRadius="full" />
              <Text>Critical (≤25%)</Text>
            </HStack>
            <HStack>
              <Box w={3} h={3} bg="orange.500" borderRadius="full" />
              <Text>Low (≤50%)</Text>
            </HStack>
            <HStack>
              <Box w={3} h={3} bg="yellow.500" borderRadius="full" />
              <Text>Warning (≤75%)</Text>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default LowStockWidget;